import { z } from 'zod';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { TransactionAllocationType } from '@/features/dashboard/financialTransactions/types/TransactionAllocation.ts';
import { TransactionDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.ts';
import { allocationTypeForDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';

/**
 * Alokacja jest płaska, choć backend ma dwa podtypy: `useFieldArray` i typy
 * błędów RHF nie radzą sobie z unią dyskryminowaną, a mapper i tak zwęża
 * kształt po kierunku transakcji przed wysyłką.
 */
const allocationSchema = z
  .object({
    type: z.enum(TransactionAllocationType),
    bookingId: z.string().default(''),
    expensePlannedTransactionId: z.string().default(''),
    // Operator wpisuje kwotę dodatnią, znak nadaje mapper zgodnie z kierunkiem.
    amount: z
      .number({ error: 'Kwota jest wymagana' })
      .positive('Kwota musi być większa niż 0'),
  })
  .superRefine((data, ctx) => {
    if (data.type === TransactionAllocationType.BOOKING && !data.bookingId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Wybierz rezerwację',
        path: ['bookingId'],
      });
    }
    if (
      data.type === TransactionAllocationType.EXPENSE &&
      !data.expensePlannedTransactionId
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Wybierz wydatek',
        path: ['expensePlannedTransactionId'],
      });
    }
  });

// Grosze, bo suma dwóch kwot po 0,1 w liczbach zmiennoprzecinkowych nie równa się 0,2.
const toCents = (value: number) => Math.round(value * 100);

/**
 * Reszta, której operator jeszcze nie rozdysponował. Puste pole kwoty przychodzi
 * z RHF jako NaN, więc liczy się je jako zero, inaczej licznik pokazuje NaN.
 */
export const getUnallocatedAmount = (
  amount: number,
  allocations: { amount: number }[],
): number => {
  const baseCents = Number.isFinite(amount) ? toCents(amount) : 0;
  const sumCents = allocations.reduce(
    (acc, allocation) =>
      acc +
      (Number.isFinite(allocation.amount) ? toCents(allocation.amount) : 0),
    0,
  );
  return (baseCents - sumCents) / 100;
};

export const financialTransactionSchema = z
  .object({
    direction: z
      .enum(TransactionDirection, { error: 'Rodzaj transakcji jest wymagany' })
      .default(TransactionDirection.INCOMING),
    /** Puste przy zapłacie za wydatek, backend przyjmuje `clientId` jako nullowalne. */
    clientId: z.string().default(''),
    amount: z
      .number({ error: 'Kwota jest wymagana' })
      .positive('Kwota musi być większa niż 0'),
    currency: z
      .enum(Currency, { error: 'Waluta jest wymagana' })
      .default(Currency.PLN),
    paymentMethod: z
      .enum(PaymentMethod, { error: 'Sposób zapłaty jest wymagany' })
      .default(PaymentMethod.TRANSFER),
    transactionDate: z.date({ error: 'Data transakcji jest wymagana' }),
    transactionTime: z
      .string({ error: 'Godzina transakcji jest wymagana' })
      .refine((val) => /^([0-1]\d|2[0-3]):([0-5]\d)$/.test(val), {
        message: 'Godzina musi być w formacie HH:mm',
      }),
    description: z
      .string()
      .max(255, { error: 'Opis może mieć maksymalnie 255 znaków' })
      .optional(),
    allocations: z
      .array(allocationSchema)
      .min(1, 'Dodaj przynajmniej jedno przypisanie'),
  })
  .superRefine((data, ctx) => {
    if (data.direction === TransactionDirection.INCOMING && !data.clientId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Wybierz zapisującego',
        path: ['clientId'],
      });
    }

    const expectedType = allocationTypeForDirection(data.direction);
    data.allocations.forEach((allocation, index) => {
      if (allocation.type !== expectedType) {
        ctx.addIssue({
          code: 'custom',
          message: 'Przypisanie nie pasuje do rodzaju transakcji',
          path: ['allocations', index, 'type'],
        });
      }
    });

    if (!Number.isFinite(data.amount)) return;
    if (data.allocations.some((a) => !Number.isFinite(a.amount))) return;

    // Backend dopuszcza resztę nierozdysponowaną i odbija dopiero przekroczenie
    // (`TRANSACTION_ALLOCATIONS_EXCEED_AMOUNT`), więc front pilnuje tego samego progu.
    const remaining = getUnallocatedAmount(data.amount, data.allocations);
    if (remaining < 0) {
      const sum = data.allocations.reduce(
        (acc, allocation) => acc + allocation.amount,
        0,
      );
      ctx.addIssue({
        code: 'custom',
        message: `Suma przypisań ${formatPrice(sum, data.currency)} przekracza kwotę transakcji ${formatPrice(data.amount, data.currency)}`,
        path: ['allocations', 'root'],
      });
    }
  });

export type FinancialTransactionFormData = z.infer<
  typeof financialTransactionSchema
>;

export type FinancialTransactionInputData = z.input<
  typeof financialTransactionSchema
>;
